import React, { useState, useCallback, useEffect, useRef } from 'react';
import { cn } from '../../utils/cn';
import { useAssetPreviewStore } from '../../store/assetPreviewStore';
import { useAssetUrl } from '../../hooks/useAssetUrl';
import { useProjectPanelStore } from '../../store/projectPanelStore';
import { formatFileSize } from '../../../core/types/asset.types';
import { ZoomIn, ZoomOut, Maximize, RotateCcw } from 'lucide-react';

interface SpritePreviewProps {
  instanceId: string;
}

const ZOOM_LEVELS = [0.1, 0.25, 0.5, 0.75, 1, 1.25, 1.5, 2, 3, 4, 5, 8, 10];
const DEFAULT_ZOOM_INDEX = 4; // 1x zoom

export const SpritePreview: React.FC<SpritePreviewProps> = ({ instanceId }) => {
  const { previews } = useAssetPreviewStore();
  const { assets } = useProjectPanelStore();

  const instance = previews[instanceId];
  const asset = instance ? assets[instance.assetId] : null;

  const { url, loading, error } = useAssetUrl(instance?.assetPath || null);

  const [zoomIndex, setZoomIndex] = useState(DEFAULT_ZOOM_INDEX);
  const [imageSize, setImageSize] = useState<{ width: number; height: number } | null>(null);
  const [imageError, setImageError] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);

  const zoom = ZOOM_LEVELS[zoomIndex];

  // Reset state when asset changes
  useEffect(() => {
    setZoomIndex(DEFAULT_ZOOM_INDEX);
    setImageSize(null);
    setImageError(false);
    setPanOffset({ x: 0, y: 0 });
  }, [instanceId]);

  const handleImageLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    setImageSize({ width: img.naturalWidth, height: img.naturalHeight });
  }, []);

  const handleImageError = useCallback(() => {
    setImageError(true);
  }, []);

  const handleZoomIn = useCallback(() => {
    setZoomIndex((prev) => Math.min(ZOOM_LEVELS.length - 1, prev + 1));
  }, []);

  const handleZoomOut = useCallback(() => {
    setZoomIndex((prev) => Math.max(0, prev - 1));
  }, []);

  const handleResetZoom = useCallback(() => {
    setZoomIndex(DEFAULT_ZOOM_INDEX);
    setPanOffset({ x: 0, y: 0 });
  }, []);

  const handleFitToView = useCallback(() => {
    if (!containerRef.current || !imageSize) return;

    const containerRect = containerRef.current.getBoundingClientRect();
    const padding = 32;
    const availableWidth = containerRect.width - padding * 2;
    const availableHeight = containerRect.height - padding * 2;

    const fitZoom = Math.min(
      availableWidth / imageSize.width,
      availableHeight / imageSize.height,
      1 // Don't scale up beyond 100%
    );

    // Find closest zoom level
    let closestIndex = 0;
    let closestDiff = Math.abs(ZOOM_LEVELS[0] - fitZoom);
    for (let i = 1; i < ZOOM_LEVELS.length; i++) {
      const diff = Math.abs(ZOOM_LEVELS[i] - fitZoom);
      if (diff < closestDiff) {
        closestDiff = diff;
        closestIndex = i;
      }
    }

    setZoomIndex(closestIndex);
    setPanOffset({ x: 0, y: 0 });
  }, [imageSize]);

  // Pan handling
  const handlePanStart = useCallback(
    (e: React.MouseEvent) => {
      if (e.button !== 0) return;

      e.preventDefault();
      setIsPanning(true);

      const startX = e.clientX;
      const startY = e.clientY;
      const startOffset = { ...panOffset };

      const handleMouseMove = (moveEvent: MouseEvent) => {
        const deltaX = moveEvent.clientX - startX;
        const deltaY = moveEvent.clientY - startY;
        setPanOffset({
          x: startOffset.x + deltaX,
          y: startOffset.y + deltaY,
        });
      };

      const handleMouseUp = () => {
        setIsPanning(false);
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.body.style.cursor = '';
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'grabbing';
    },
    [panOffset]
  );

  // Mouse wheel zoom - use native event listener with passive: false to allow preventDefault
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      if (e.deltaY < 0) {
        setZoomIndex((prev) => Math.min(ZOOM_LEVELS.length - 1, prev + 1));
      } else {
        setZoomIndex((prev) => Math.max(0, prev - 1));
      }
    };

    container.addEventListener('wheel', handleWheel, { passive: false });
    return () => {
      container.removeEventListener('wheel', handleWheel);
    };
  }, []);

  if (!instance || !asset) {
    return (
      <div className="flex-1 flex items-center justify-center text-zinc-500">
        Asset not found
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Toolbar */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-zinc-700 bg-zinc-800/50">
        <button
          className="p-1.5 rounded hover:bg-zinc-700 text-zinc-400 hover:text-zinc-200 transition-colors"
          onClick={handleZoomOut}
          disabled={zoomIndex === 0}
          title="Zoom Out"
        >
          <ZoomOut size={16} />
        </button>
        <span className="text-xs text-zinc-400 min-w-[48px] text-center">
          {Math.round(zoom * 100)}%
        </span>
        <button
          className="p-1.5 rounded hover:bg-zinc-700 text-zinc-400 hover:text-zinc-200 transition-colors"
          onClick={handleZoomIn}
          disabled={zoomIndex === ZOOM_LEVELS.length - 1}
          title="Zoom In"
        >
          <ZoomIn size={16} />
        </button>
        <div className="w-px h-4 bg-zinc-700 mx-1" />
        <button
          className="p-1.5 rounded hover:bg-zinc-700 text-zinc-400 hover:text-zinc-200 transition-colors"
          onClick={handleResetZoom}
          title="Reset (100%)"
        >
          <RotateCcw size={16} />
        </button>
        <button
          className="p-1.5 rounded hover:bg-zinc-700 text-zinc-400 hover:text-zinc-200 transition-colors"
          onClick={handleFitToView}
          title="Fit to View"
        >
          <Maximize size={16} />
        </button>
      </div>

      {/* Image container */}
      <div
        ref={containerRef}
        className={cn(
          'flex-1 overflow-hidden',
          'bg-checkerboard',
          'flex items-center justify-center',
          !isPanning && 'cursor-grab',
          isPanning && 'cursor-grabbing'
        )}
        onMouseDown={handlePanStart}
      >
        {loading ? (
          <div className="w-32 h-32 rounded bg-zinc-700 animate-pulse" />
        ) : error || imageError ? (
          <div className="text-center text-zinc-500">
            <div className="text-4xl mb-2">⚠️</div>
            <div className="text-sm">Failed to load image</div>
          </div>
        ) : url ? (
          <img
            src={url}
            alt={asset.name}
            className="max-w-none"
            style={{
              transform: `translate(${panOffset.x}px, ${panOffset.y}px) scale(${zoom})`,
              transition: isPanning ? 'none' : 'transform 0.1s ease-out',
              imageRendering: zoom > 1 ? 'pixelated' : 'auto',
            }}
            onLoad={handleImageLoad}
            onError={handleImageError}
            draggable={false}
          />
        ) : null}
      </div>

      {/* Metadata footer */}
      <div className="flex items-center gap-4 px-3 py-2 border-t border-zinc-700 bg-zinc-800/50 text-xs text-zinc-400">
        {imageSize && (
          <>
            <span>
              {imageSize.width} × {imageSize.height} px
            </span>
            <span className="text-zinc-600">|</span>
          </>
        )}
        <span>{formatFileSize(asset.metadata?.fileSize || 0)}</span>
        <span className="text-zinc-600">|</span>
        <span className="uppercase">{asset.extension.slice(1)}</span>
      </div>
    </div>
  );
};
